/**
 * t239u: the phone acknowledgement status page. One progress line, two sideways chips (Outstanding / Acknowledged), and
 * whether the sticky Acknowledge button shows. Platform-neutral for React Native parity.
 */
import type { AckRecipientView, AckView } from '../acks';
import type { Announcement, AnnouncementStatsRow } from '../kb/types';

export type AckSeg = 'outstanding' | 'acknowledged';

export function ackProgressText(c: AckView['counts']): string {
  return `${c.acknowledged} of ${c.total} acknowledged`;
}
export function ackProgressPct(c: AckView['counts']): number {
  return c.total ? Math.round((c.acknowledged / c.total) * 100) : 0;
}
/** Outstanding = still owes one (incl. re-ack after an edit); Acknowledged = done, an earlier version, or left. */
export function ackSegments(v: AckView): Record<AckSeg, AckRecipientView[]> {
  const list = v.recipients ?? [];
  const byName = (a: AckRecipientView, b: AckRecipientView) => a.display_name.localeCompare(b.display_name);
  return {
    outstanding: list.filter(r => r.status === 'outstanding').sort(byName),
    acknowledged: list.filter(r => r.status !== 'outstanding').sort((a, b) => (b.acknowledged_at ?? '').localeCompare(a.acknowledged_at ?? '') || byName(a, b)),
  };
}
/** The sticky Acknowledge button: only for a recipient who still owes it, while the request is live. */
/** The phone status page for an acknowledgement message. */
export const ackPageHref = (messageId: string) => `/acknowledgements/${messageId}`;
/** Phone bell: acknowledgement notifications open the status page; everything else keeps its own link. */
export function phoneNotificationLink(n: { kind: string; entity_type: string | null; entity_id: string | null; link: string | null }): string | null {
  return n.kind.startsWith('ack.') && n.entity_type === 'message' && n.entity_id ? ackPageHref(n.entity_id) : n.link;
}
export function ackShowButton(v: AckView): boolean {
  return !v.cancelled_at && v.my_status === 'outstanding';
}

// ------------------------------------------------------------------------------------------------ t357u announcements
/**
 * t357u: the owner and managers (whoever the API sends `stats` to: comms.announcement.stats / publish over the audience)
 * see progress on every announcement that asks for acknowledgement, not only their own. Staff get stats: null, so nothing.
 */
export function announcementAckLine(a: Pick<Announcement, 'require_ack' | 'stats'>): string | null {
  if (!a.require_ack || !a.stats) return null;
  return `${a.stats.acked}/${a.stats.audience} acknowledged`;
}
/** The phone status page, shared with chat acknowledgements. */
export const announcementAckHref = (id: string) => `/acknowledgements/${id}?kind=announcement`;
/**
 * An announcement + its stats rows as the AckView the status page draws: everyone in the audience (not the author), the
 * current version acknowledged = Acknowledged, anything else (incl. an earlier version) = Outstanding. Read-only: there is
 * no manual Remind for announcements (the worker reminds 24h before the deadline).
 */
export function announcementAckView(a: Announcement, rows: AnnouncementStatsRow[] | undefined, me: string | null | undefined): AckView {
  const recipients: AckRecipientView[] | undefined = rows?.map(r => ({
    user_id: r.user_id, display_name: r.display_name, source: 'snapshot', status: r.acked_version === a.version ? 'acknowledged' : 'outstanding',
    acknowledged_at: r.acked_at, acknowledged_version: r.acked_version, added_at: a.publish_at,
  }));
  const total = recipients ? recipients.length : a.stats?.audience ?? 0;
  const acknowledged = recipients ? recipients.filter(r => r.status === 'acknowledged').length : a.stats?.acked ?? 0;
  const mine = me ? rows?.find(r => r.user_id === me) : undefined;
  const asked = a.require_ack && a.author_id !== me;
  return {
    message_id: a.id, conversation_id: '', audience: 'all', include_new_members: true, team_names: [], due_at: a.ack_due_at, require_reack: true,
    content_version: a.version, content_changed_at: a.version > 1 ? a.updated_at : null, created_by: a.author_id, created_by_name: a.author_name,
    created_at: a.publish_at, cancelled_at: a.require_ack ? null : a.updated_at,
    counts: { total, acknowledged, outstanding: total - acknowledged, changed: 0, left: 0 },
    my_status: asked ? (a.ack_required ? 'outstanding' : 'acknowledged') : null,
    my_acknowledged_at: mine?.acked_at ?? null, can_manage: false, recipients,
    preview: a.title, conversation_name: 'Announcement', link: `/announcements/${a.id}`,
  };
}
